<script setup lang="ts">
import { useHelpers, useTranslate } from '@/shared/lib'
import { ref } from 'vue'
import { useConfirm, VIcons } from '@/shared/ui'
import ProgramCabinetChart from '@/entities/Schedule/Program/ui/ProgramCabinetChart.vue'
import { cloneDeep } from 'lodash'
import { useAuth } from '@/entities/Auth'

defineProps({
  month: {
    type: Number,
    required: true,
  },
  day: {
    type: Number,
    required: true,
  },
  outputs: {
    type: Number,
    required: true,
  },
})

const item = defineModel<any>()
const { monthsList } = useHelpers()
const { trs } = useTranslate()
const { show } = useConfirm()
const { isPermissions } = useAuth()

const output = ref(0)
const levelList = [
  { title: trs('itf.off_short'), value: 0 },
  { title: trs('itf.on_short'), value: 1 },
]

const emits = defineEmits(['remove'])

/**
 * Функция удаления переключения
 * @param key
 */
const removeSwitchHandler = (key: number) => {
  show({
    content: trs('itf.confirm_remove_switch'),
    confirmFunc: () => item.value[output.value].splice(key, 1),
  })
}

/**
 * Функция добавления переключения
 */
const addSwitchHandler = () => {
  item.value[output.value].push([0, 0, 0])
}

/**
 * Функция сортировки переключений при изменении
 * @param event
 */
const changeSwitchHandler = (event: InputEvent) => {
  const target = event.target as HTMLInputElement
  target.blur()

  item.value[output.value] = item.value[output.value].sort((a: number[], b: number[]) => {
    const aSum = a[0] * 60 + Number(a[1])
    const bSum = b[0] * 60 + Number(b[1])
    return aSum - bSum
  })
}

/**
 * Функция применения переключений ко всем выходам
 */
const applyOutputsHandler = () => {
  show({
    content: trs('itf.confirm_apply_all_outputs'),
    confirmFunc: () => {
      for (let key in item.value) {
        item.value[key] = cloneDeep(item.value[output.value])
      }
    },
  })
}

/**
 * Функция удаления расписания
 */
const removeHandler = () => {
  show({
    content: trs('itf.confirm_remove_program'),
    confirmFunc: () => emits('remove'),
  })
}

/**
 * Фильтруем вводимые пользователем данные
 * @param max
 * @param item
 * @param key
 * @param index
 */
const filterHandler = (max: number, item: any, key: number, index: number) => {
  let value = Number(item[output.value][key][index])

  if (value < 0) {
    value = 0
  } else if (value > max) {
    value = max
  }

  item[output.value][key][index] = String(value)
}
</script>

<template>
  <div class="schedule-program__item">
    <div class="schedule-program__header">
      {{ day }} {{ monthsList(true)[month - 1].title.toLowerCase() }}
    </div>

    <div class="schedule-program__charts">
      <ProgramCabinetChart :data="item[key - 1] || []" v-for="key in outputs" />
    </div>

    <div class="schedule-program__outputs">
      <div>{{ $t('itf.outputs') }}:</div>

      <div class="schedule-program__row">
        <div
          class="schedule-program__row-item"
          :class="{ 'schedule-program__row-item_active': key - 1 === output }"
          v-for="key in outputs"
          @click="output = key - 1"
        >
          {{ key }}
        </div>
      </div>
    </div>

    <form class="schedule-program__form">
      <div class="schedule-program__form-row" v-for="(_, key) in item[output]">
        <VTextField
          type="number"
          v-model="item[output][key][0]"
          :label="$t('itf.hours')"
          :disabled="!isPermissions(['SСHEDULE_EDIT'])"
          hide-details
          @keyup.enter="changeSwitchHandler"
          @input="filterHandler(23, item, key, 0)"
        />

        <VTextField
          type="number"
          v-model="item[output][key][1]"
          :label="$t('itf.minute')"
          :disabled="!isPermissions(['SСHEDULE_EDIT'])"
          hide-details
          @keyup.enter="changeSwitchHandler"
          @input="filterHandler(59, item, key, 1)"
        />

        <VSelect
          v-model="item[output][key][2]"
          :label="$t('itf.level')"
          :disabled="!isPermissions(['SСHEDULE_EDIT'])"
          hide-details
          :items="levelList"
        />

        <div>
          <VBtn
            color="error"
            variant="text"
            v-if="item[output].length > 1"
            @click="removeSwitchHandler(key)"
            :class="{ disabled: !isPermissions(['SСHEDULE_EDIT']) }"
          >
            <VIcons type="delete" />
          </VBtn>
        </div>
      </div>
    </form>

    <div v-if="isPermissions(['SСHEDULE_EDIT'])" class="schedule-program__options">
      <VBtn
        id="add-button"
        icon
        density="compact"
        color="primary"
        variant="text"
        @click.prevent="addSwitchHandler"
      >
        <VIcons type="add" />
      </VBtn>

      <VTooltip
        :text="$t('itf.add_switch')"
        location="top"
        :open-delay="1000"
        activator="#add-button"
      />

      <VBtn
        id="apply-button"
        icon
        density="compact"
        color="warning"
        variant="text"
        @click.prevent="applyOutputsHandler"
      >
        <VIcons type="event_available" />
      </VBtn>

      <VTooltip
        :text="$t('itf.apply_all_outputs')"
        location="top"
        :open-delay="1000"
        activator="#apply-button"
      />

      <VBtn
        id="remove-button"
        icon
        density="compact"
        color="error"
        @click="removeHandler"
        variant="text"
      >
        <VIcons type="delete" />
      </VBtn>

      <VTooltip
        :text="$t('itf.delete')"
        location="top"
        :open-delay="1000"
        activator="#remove-button"
      />
    </div>
  </div>
</template>
